
import { useState, useEffect } from 'react';
import { PersonalityType, CareerPreferences, MBTIResult, mbtiCareerExtensions } from '@/utils/personalityTypes';
import { getCareerMatchesForType, Career, getTraditionalCareers } from '@/utils/careerData';
import { cn } from '@/lib/utils';

interface CareerMatchProps {
  result: { 
    personalityType: PersonalityType | null,
    careerPreferences: CareerPreferences | null,
    mbtiResult: MBTIResult | null
  };
}

const CareerMatch = ({ result }: CareerMatchProps) => {
  const [matchedCareers, setMatchedCareers] = useState<Career[]>([]);
  const [traditionalCareers, setTraditionalCareers] = useState<Career[]>([]);
  const [mbtiCareers, setMbtiCareers] = useState<any[]>([]);
  const [selectedCareer, setSelectedCareer] = useState<Career | null>(null);
  const [activeTab, setActiveTab] = useState<'future' | 'traditional' | 'mbti'>('mbti');
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    if (result.personalityType && result.mbtiResult) {
      // Simulate loading to show animation
      setIsLoading(true);
      setTimeout(() => {
        // Get future careers based on personality type
        const futureMatches = getCareerMatchesForType(result.personalityType!.id, false);
        
        // Get traditional careers based on personality traits
        const topTraits = Object.entries(result.personalityType!.traits)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 2)
          .map(entry => entry[0]);
        
        const traditionalMatches = getTraditionalCareers()
          .filter(career => 
            career.requiredTraits.some(trait => topTraits.includes(trait))
          )
          .slice(0, 5); // Ensure at least 5 traditional careers
        
        // Get MBTI-specific careers
        const mbtiType = result.mbtiResult.type;
        const mbtiSpecificCareers = mbtiCareerExtensions
          .filter(career => career.connections.includes(mbtiType))
          .map(career => ({
            id: career.id,
            title: career.title,
            description: career.description,
            mbtiMatch: true
          }));
          
        // Use career preferences to determine default tab
        if (result.careerPreferences) {
          const { traditionalVsEmerging } = result.careerPreferences;
          
          // If user has a strong preference for traditional careers, 
          // show the traditional tab by default
          if (traditionalVsEmerging < -2) {
            setActiveTab('traditional');
          } else {
            // Otherwise show MBTI tab by default
            setActiveTab('mbti');
          }
        }
        
        // Ensure we have at least 5 career options in each category
        while (futureMatches.length < 5 && traditionalMatches.length > 5) {
          futureMatches.push(traditionalMatches.pop()!);
        }
        
        while (traditionalMatches.length < 5 && futureMatches.length > 5) {
          traditionalMatches.push(futureMatches.pop()!);
        }
        
        setMatchedCareers(futureMatches);
        setTraditionalCareers(traditionalMatches);
        setMbtiCareers(mbtiSpecificCareers);
        
        // Select the first career from the active tab
        if (activeTab === 'future' && futureMatches.length > 0) {
          setSelectedCareer(futureMatches[0]);
        } else if (activeTab === 'traditional' && traditionalMatches.length > 0) {
          setSelectedCareer(traditionalMatches[0]);
        } else if (mbtiSpecificCareers.length > 0) {
          // For MBTI tab, we'll show a different UI so no need to select a career
        }
        
        setIsLoading(false);
      }, 800);
    }
  }, [result]);
  
  const handleTabChange = (tab: 'future' | 'traditional' | 'mbti') => {
    setActiveTab(tab);
    // Select the first career from the active tab
    if (tab === 'future' && matchedCareers.length > 0) {
      setSelectedCareer(matchedCareers[0]);
    } else if (tab === 'traditional' && traditionalCareers.length > 0) {
      setSelectedCareer(traditionalCareers[0]);
    } else {
      // For MBTI tab, we'll show a different UI so no need to select a career
      setSelectedCareer(null);
    }
  };

  const handleCareerSelect = (career: Career) => {
    setSelectedCareer(career);
    // Change to the appropriate tab based on the career type
    if (career.isTraditional) {
      setActiveTab('traditional');
    } else {
      setActiveTab('future');
    }
  };

  const renderMBTIResults = () => {
    if (!result.mbtiResult) return null;
    
    const { type, details, dimensionScores } = result.mbtiResult;
    
    // Calculate percentage for each dimension
    const eiPercentage = Math.abs(dimensionScores.EI) / 10 * 100;
    const snPercentage = Math.abs(dimensionScores.SN) / 10 * 100;
    const tfPercentage = Math.abs(dimensionScores.TF) / 10 * 100;
    const jpPercentage = Math.abs(dimensionScores.JP) / 10 * 100;
    
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 shadow-soft border border-white/20">
          <h3 className="text-2xl font-bold mb-2">{type}: {details.name}</h3>
          <p className="text-muted-foreground mb-4">{details.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <h4 className="font-medium mb-2">Key Strengths</h4>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                {details.strengths.map((strength, i) => (
                  <li key={i}>{strength}</li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Potential Challenges</h4>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                {details.challenges.map((challenge, i) => (
                  <li key={i}>{challenge}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 shadow-soft border border-white/20">
          <h3 className="font-medium mb-4">Your Personality Dimensions</h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">Extraversion (E)</span>
                <span className="font-medium">Introversion (I)</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${dimensionScores.EI >= 0 ? 'bg-blue-500 rounded-l-full' : 'bg-purple-500 rounded-r-full ml-auto'}`}
                  style={{ width: `${eiPercentage}%` }}
                ></div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                You tend to be more {dimensionScores.EI >= 0 ? 'extraverted, gaining energy from social interaction' : 'introverted, gaining energy from solitary activities'}
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">Sensing (S)</span>
                <span className="font-medium">Intuition (N)</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${dimensionScores.SN >= 0 ? 'bg-green-500 rounded-l-full' : 'bg-pink-500 rounded-r-full ml-auto'}`}
                  style={{ width: `${snPercentage}%` }}
                ></div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                You tend to focus on {dimensionScores.SN >= 0 ? 'concrete facts and details' : 'patterns and possibilities'}
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">Thinking (T)</span>
                <span className="font-medium">Feeling (F)</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${dimensionScores.TF >= 0 ? 'bg-yellow-500 rounded-l-full' : 'bg-red-500 rounded-r-full ml-auto'}`}
                  style={{ width: `${tfPercentage}%` }}
                ></div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                You tend to make decisions based on {dimensionScores.TF >= 0 ? 'logical analysis' : 'personal values and empathy'}
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">Judging (J)</span>
                <span className="font-medium">Perceiving (P)</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${dimensionScores.JP >= 0 ? 'bg-orange-500 rounded-l-full' : 'bg-teal-500 rounded-r-full ml-auto'}`}
                  style={{ width: `${jpPercentage}%` }}
                ></div>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                You prefer {dimensionScores.JP >= 0 ? 'structure and planning' : 'flexibility and spontaneity'}
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 shadow-soft border border-white/20">
          <h3 className="font-medium mb-4">Recommended Careers for {type}</h3>
          <p className="text-muted-foreground mb-4">Based on your MBTI personality type, these careers match well with your natural preferences and strengths:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.compatibleCareers.slice(0, 6).map((careerId, index) => {
              // Find the career in either future or traditional careers
              const career = [...matchedCareers, ...traditionalCareers].find(c => c.id === careerId);
              
              if (!career) return null;
              
              return (
                <button 
                  key={index} 
                  className="text-left border rounded-lg p-4 hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer"
                  onClick={() => handleCareerSelect(career)}
                >
                  <h4 className="font-medium mb-1">{career.title}</h4>
                  <p className="text-sm text-muted-foreground">{career.description.substring(0, 100)}...</p>
                  
                  <div className="flex items-center mt-3">
                    <span className="text-xs font-medium mr-2">Future Relevance</span>
                    <div className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                      {career.futureRelevance}/10
                    </div>
                  </div>
                </button>
              );
            })}
            
            {mbtiCareers.map((career, index) => (
              <div key={`mbti-${index}`} className="border rounded-lg p-4 border-secondary/30 bg-secondary/5">
                <h4 className="font-medium mb-1">{career.title}</h4>
                <p className="text-sm text-muted-foreground">{career.description}</p>
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/20 text-secondary">
                    MBTI Match
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  if (!result.personalityType) {
    return null;
  }
  
  return (
    <div className="w-full max-w-4xl mx-auto px-4">
      <div className="mb-8 text-center animate-fade-in">
        <div className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-3">
          Your Results
        </div>
        <h2 className="text-3xl font-bold mb-3">Your Career Assessment Results</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          We've analyzed your responses to identify your MBTI personality type and matched it with future-ready careers
        </p>
      </div>
      
      {/* Career preference insights */}
      {result.careerPreferences && (
        <div className="bg-muted/30 rounded-lg p-4 mb-8 text-center">
          <h3 className="text-sm font-medium text-secondary mb-2">Your Career Preferences</h3>
          <div className="flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
              {result.careerPreferences.traditionalVsEmerging > 0 ? 'Emerging Careers' : 'Traditional Careers'}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
              {result.careerPreferences.technicalVsHuman > 0 ? 'Technical Focus' : 'Human-Centered'}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
              {result.careerPreferences.specializedVsAdaptable > 0 ? 'Adaptable Skills' : 'Specialized Expertise'}
            </span>
          </div>
        </div>
      )}
      
      {/* Tabs for career types */}
      <div className="flex border-b border-border mb-6">
        <button
          onClick={() => handleTabChange('mbti')}
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors relative",
            activeTab === 'mbti' 
              ? "text-primary" 
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          MBTI Results
          {activeTab === 'mbti' && (
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></span>
          )}
        </button>
        <button
          onClick={() => handleTabChange('future')}
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors relative",
            activeTab === 'future' 
              ? "text-primary" 
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Future Careers
          {activeTab === 'future' && (
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></span>
          )}
        </button>
        <button
          onClick={() => handleTabChange('traditional')}
          className={cn(
            "px-4 py-2 font-medium text-sm transition-colors relative",
            activeTab === 'traditional' 
              ? "text-primary" 
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          Traditional Careers
          {activeTab === 'traditional' && (
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></span>
          )}
        </button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse-soft flex flex-col items-center">
            <div className="h-12 w-48 bg-muted rounded-lg mb-4"></div>
            <div className="h-4 w-64 bg-muted rounded mb-2"></div>
            <div className="h-4 w-56 bg-muted rounded mb-2"></div>
            <div className="h-4 w-60 bg-muted rounded"></div>
          </div>
        </div>
      ) : (
        <>
          {activeTab === 'mbti' ? (
            renderMBTIResults()
          ) : (
            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-4 space-y-2">
                <h3 className="font-medium text-lg mb-3">
                  {activeTab === 'future' 
                    ? 'Your Top Future Career Matches' 
                    : 'Your Traditional Career Matches'}
                </h3>
                <div className="space-y-2">
                  {(activeTab === 'future' ? matchedCareers : traditionalCareers).map((career) => (
                    <button
                      key={career.id}
                      onClick={() => setSelectedCareer(career)}
                      className={cn(
                        "w-full text-left px-4 py-3 rounded-lg transition-all duration-200",
                        "border hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20",
                        selectedCareer?.id === career.id
                          ? "border-primary/50 bg-primary/5 shadow-sm"
                          : "border-border bg-white/50"
                      )}
                    >
                      <div className="font-medium">{career.title}</div>
                      <div className="text-sm text-muted-foreground">
                        {career.isTraditional
                          ? "Traditional career path" 
                          : career.emergenceTimeframe === "current" 
                            ? "Available now" 
                            : career.emergenceTimeframe === "emerging"
                              ? "Emerging in 1-5 years"
                              : "Predicted in 5-10 years"}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="md:col-span-8">
                {selectedCareer && (
                  <div className="glass-card rounded-xl p-6 h-full animate-fade-in">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold mb-1">{selectedCareer.title}</h3>
                        {selectedCareer.traditionalEquivalent && !selectedCareer.isTraditional && (
                          <div className="text-sm text-muted-foreground">
                            Evolution of: <span className="font-medium">{selectedCareer.traditionalEquivalent}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center">
                        <div className="text-xs font-medium mr-2">Future Relevance</div>
                        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                          {selectedCareer.futureRelevance}/10
                        </div>
                      </div>
                    </div>
                    
                    <p className="mb-4 text-foreground/90">{selectedCareer.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="text-sm font-medium text-secondary mb-2">Key Skills Needed</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedCareer.keySkills.map((skill, index) => (
                            <span 
                              key={index} 
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary/10 text-secondary"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-medium text-secondary mb-2">Education Paths</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedCareer.educationPaths.map((path, index) => (
                            <span 
                              key={index} 
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                            >
                              {path}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Job Responsibilities Section */}
                    <div className="border-t border-border pt-4 mt-4">
                      <h4 className="text-sm font-medium text-secondary mb-2">Key Job Responsibilities</h4>
                      <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                        <li>Analyzing data and information related to {selectedCareer.title.toLowerCase()} industry trends</li>
                        <li>Collaborating with cross-functional teams to implement solutions</li>
                        <li>Staying updated with the latest developments in {selectedCareer.keySkills[0]?.toLowerCase() || "the field"}</li>
                        <li>Creating reports and presentations for stakeholders</li>
                        <li>Developing strategies to optimize outcomes in {selectedCareer.title.toLowerCase()} processes</li>
                      </ul>
                    </div>
                    
                    {/* Job Prospects Section */}
                    <div className="border-t border-border pt-4 mt-4">
                      <h4 className="text-sm font-medium text-secondary mb-2">Future Job Prospects</h4>
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <div className="flex items-center mb-2">
                          <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                          <span className="font-medium">Growth Outlook:</span>
                          <span className="ml-2">
                            {selectedCareer.futureRelevance >= 8 
                              ? "Excellent" 
                              : selectedCareer.futureRelevance >= 6 
                                ? "Good" 
                                : "Moderate"}
                          </span>
                        </div>
                        <div className="flex items-center mb-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                          <span className="font-medium">Salary Potential:</span>
                          <span className="ml-2">
                            {selectedCareer.futureRelevance >= 8 
                              ? "Above Average" 
                              : selectedCareer.futureRelevance >= 6 
                                ? "Average" 
                                : "Entry Level"}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 rounded-full bg-purple-500 mr-2"></div>
                          <span className="font-medium">Remote Work Potential:</span>
                          <span className="ml-2">
                            {selectedCareer.keySkills.some(skill => 
                              skill.toLowerCase().includes("digital") || 
                              skill.toLowerCase().includes("remote") || 
                              skill.toLowerCase().includes("tech")
                            ) 
                              ? "High" 
                              : "Moderate"}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-border pt-4 mt-4">
                      <h4 className="text-sm font-medium text-secondary mb-2">
                        {selectedCareer.isTraditional ? "How This Career Will Evolve" : "Why This Career Will Grow"}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCareer.industryTrends.map((trend, index) => (
                          <span 
                            key={index} 
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                          >
                            {trend}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mt-4 border-t border-border pt-4">
                      <h4 className="text-sm font-medium text-secondary mb-2">AI Automation Risk:</h4>
                      <div className="flex items-center">
                        <div className="w-full bg-muted rounded-full h-2.5">
                          <div 
                            className="h-2.5 rounded-full transition-all duration-500 ease-out" 
                            style={{ 
                              width: `${selectedCareer.aiRiskLevel * 10}%`, 
                              backgroundColor: `hsl(${120 - selectedCareer.aiRiskLevel * 12}, 100%, 40%)`
                            }}
                          />
                        </div>
                        <div className="ml-2 text-sm font-medium flex items-center">
                          <span className="mr-1">{selectedCareer.aiRiskLevel}/10</span>
                          <span className="text-xs text-muted-foreground">
                            {selectedCareer.aiRiskLevel <= 3 
                              ? "(Low risk)" 
                              : selectedCareer.aiRiskLevel <= 6 
                                ? "(Medium risk)" 
                                : "(High risk)"}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {selectedCareer.aiRiskLevel <= 3 
                          ? "This career requires human creativity, judgment, and social skills that are difficult to automate." 
                          : selectedCareer.aiRiskLevel <= 6 
                            ? "Some aspects of this role may be automated, but core human skills will remain essential." 
                            : "This field may be heavily impacted by automation; focus on developing uniquely human capabilities."}
                      </p>
                    </div>
                    
                    {/* Work Environment Section */}
                    <div className="mt-4 border-t border-border pt-4">
                      <h4 className="text-sm font-medium text-secondary mb-2">Typical Work Environment</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-muted/20 p-2 rounded-lg">
                          <span className="font-medium block mb-1">Settings:</span>
                          <p className="text-sm text-muted-foreground">
                            {selectedCareer.futureRelevance >= 8 
                              ? "Innovative startups, research institutions, tech companies" 
                              : "Corporate environments, consulting firms, government agencies"}
                          </p>
                        </div>
                        <div className="bg-muted/20 p-2 rounded-lg">
                          <span className="font-medium block mb-1">Collaboration Style:</span>
                          <p className="text-sm text-muted-foreground">
                            {selectedCareer.keySkills.some(skill => 
                              skill.toLowerCase().includes("team") || 
                              skill.toLowerCase().includes("communication") || 
                              skill.toLowerCase().includes("collaboration")
                            ) 
                              ? "Highly collaborative with cross-functional teams" 
                              : "Balance of independent work and team collaboration"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CareerMatch;
